import React from 'react';
import { FaUserGraduate, FaHistory, FaTrophy, FaFlask, FaCalendarAlt, FaHandshake, FaLightbulb, FaHeart, FaRocket } from 'react-icons/fa';
import usePageTitle from '../hooks/usePageTitle';
import { useLanguage } from '../contexts/LanguageContext';

const About = () => {
  usePageTitle();
  const { t } = useLanguage();
  const values = [
    { icon: <FaHandshake className="text-2xl" />, title: t('about.collaboration'), desc: t('about.collaborationDesc') },
    { icon: <FaLightbulb className="text-2xl" />, title: t('about.innovation'), desc: t('about.innovationDesc') },
    { icon: <FaHeart className="text-2xl" />, title: t('about.dedication'), desc: t('about.dedicationDesc') },
    { icon: <FaRocket className="text-2xl" />, title: t('about.acceleration'), desc: t('about.accelerationDesc') },
  ];

  const pkmTimeline = [
    { year: '1988', event: 'Lomba Karya Ilmiah Mahasiswa (LKIM) pertama kali terselenggara di Universitas Indonesia (UI).' },
    { year: '1990', event: 'Istilah "PIMNAS" pertama kali muncul saat acara terselenggara di Institut Pertanian Bogor (IPB).' },
    { year: '2004', event: 'Format juara umum dengan piala "Adhikarta Kertawidya" muncul pertama kali di PIMNAS ke-17 di STT-Telkom Bandung.' },
    { year: '2009', event: 'PKM dipecah menjadi 6 skema: PKM-P, PKM-K, PKM-M, PKM-T, PKM-AI, dan PKM-GT.' },
    { year: '2011', event: 'Bertambah menjadi 7 bidang dengan hadirnya PKM-Karsa Cipta (PKM-KC).' },
    { year: '2019', event: 'PKM-GFK (Gagasan Futuristik Konstruktif) berbasis media sosial diperkenalkan.' },
    { year: '2021', event: 'PKM-P → PKM-Riset (PKM-R), PKM-M → PKM-PM, PKM-T → PKM-PI (Penerapan Iptek).' },
    { year: '2022', event: 'PKM-RE dan PKM-RSH terpisah. PKM-GT → PKM-GFT, PKM-GFK → PKM-VGK.' },
  ];

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FaUserGraduate className="text-gold-400 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('about.title')}</h1>
          <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Apa itu PIMNAS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch mb-20">
          <div className="bg-gradient-to-br from-primary-800 to-primary-900 rounded-2xl p-8 text-white flex flex-col justify-center">
            <h2 className="text-2xl font-bold mb-6 text-gold-400 flex items-center gap-3">
              <FaFlask /> {t('about.whatIsPimnas')}
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">{t('about.whatIsPimnas')}</strong> {t('about.whatIsPimnasDesc1')}
            </p>
            <p className="text-gray-300 leading-relaxed">
              {t('about.whatIsPimnasDesc2')}
            </p>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <FaHistory /> {t('about.pimnasHistory')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {t('about.fromLKIM')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t('about.historyDesc1')}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t('about.historyDesc2')}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {t('about.historyDesc3')}
            </p>
          </div>
        </div>

        {/* PKM Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-gold-100 text-gold-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-3">
              <FaFlask /> {t('about.pkmProgram')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('about.pkmChanges')}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {t('about.pkmDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">{t('about.initialSchemes')}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 shrink-0"></span> PKM-Penelitian (PKM-P)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 shrink-0"></span> PKM-Kewirausahaan (PKM-K)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 shrink-0"></span> PKM-Pengabdian kepada Masyarakat (PKM-M)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 shrink-0"></span> PKM-Penerapan Teknologi (PKM-T)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary-500 shrink-0"></span> PKM-Penulisan Ilmiah (PKM-I)</div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">{t('about.latestDev')}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold-500 shrink-0"></span> <strong>2009:</strong> +PKM-AI & PKM-GT (6 skema)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold-500 shrink-0"></span> <strong>2011:</strong> +PKM-Karsa Cipta (7 skema)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold-500 shrink-0"></span> <strong>2019:</strong> +PKM-GFK (berbasis media sosial)</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold-500 shrink-0"></span> <strong>2021:</strong> PKM-P → PKM-R, PKM-T → PKM-PI</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gold-500 shrink-0"></span> <strong>2022:</strong> PKM-GT → PKM-GFT, PKM-GFK → PKM-VGK</div>
              </div>
            </div>
          </div>
        </div>

        {/* Adhikarta Kertawidya */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          <div className="lg:col-span-1 bg-gradient-to-br from-gold-400 to-gold-600 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <FaTrophy className="text-white text-6xl mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Adhikarta Kertawidya</h3>
            <p className="text-white/90 text-sm">{t('about.adhikartaDesc')}</p>
          </div>
          <div className="lg:col-span-2 flex flex-col justify-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('about.pimnasAward')}</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t('about.awardDesc1')}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t('about.awardDesc2')}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {t('about.awardDesc3')}
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
              <FaCalendarAlt className="text-primary-500" /> {t('about.timeline')}
            </h2>
            <p className="text-gray-500">{t('about.timelineDesc')}</p>
          </div>
          <div className="space-y-6">
            {pkmTimeline.map((t, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-gold-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {t.year}
                </div>
                <div className="pt-3 border-b border-gray-50 pb-4 flex-1">
                  <p className="text-gray-700 leading-relaxed">{t.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('about.ourValues')}</h2>
          <p className="text-gray-500">{t('about.valuesDesc')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {values.map((v, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 text-center hover:shadow-lg transition-all transform hover:-translate-y-1">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
                {v.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{v.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default About;
